import classNames from 'classnames';

import { MouseEventHandler } from 'react';
import { Album as DownloadAlbum } from '@bpm-web-app/download-api-sdk';
import { ThreeDotsSheetOptionsKeys, THREE_DOTS_SHEET_KEYS } from '../../three-dots-sheet/three-dots-sheet.context';
import styles from './quick-actions-column.module.css';

import AddToPlaylistIcon from '../../../../assets/icons/supreme/add-to-playlist.svg';
import ThreeDotsIcon from '../../../../assets/icons/supreme/three-dots.svg';
import AddToCrate from '../../../../assets/icons/add-to-crate.svg';
import RemoveFromCrate from '../../../../assets/icons/remove-from-crate.svg';

// openThreeDots is not a simple MouseEventHandler because some entry points
// (e.g., Add to Crate) need to open the sheet with a specific option preselected.
// The optional initialKey allows callers to control the sheet's initial state.
type OpenThreeDotsHandler = (e: React.MouseEvent<HTMLButtonElement>, initialKey?: ThreeDotsSheetOptionsKeys) => void;

interface QuickActionsColumnBaseProps {
    embedded?: boolean;
}

interface DownloadQuickActionsColumnProps extends QuickActionsColumnBaseProps {
    variant?: 'download';
    openPlaylists: MouseEventHandler<HTMLButtonElement>;
    openThreeDots: OpenThreeDotsHandler;
    downloadAlbum?: DownloadAlbum;
    addDownloadTrackToCrate?: (mediaId: number) => void;
}

interface CrateQuickActionsColumnProps extends QuickActionsColumnBaseProps {
    variant: 'crate';
    onRemoveFromCrate: MouseEventHandler<HTMLButtonElement>;
    onOpenThreeDots: MouseEventHandler<HTMLButtonElement>;
}

export type QuickActionsColumnProps = DownloadQuickActionsColumnProps | CrateQuickActionsColumnProps;

export function QuickActionsColumn(props: QuickActionsColumnProps) {
    const { embedded, variant } = props;

    if (variant === 'crate') {
        const { onRemoveFromCrate, onOpenThreeDots } = props;

        return (
            <div
                className={classNames('quick-actions-column', styles['quick-actions-column'], {
                    'quick-actions-column--embedded': embedded,
                    [styles['quick-actions-column--embedded']]: embedded,
                })}
            >
                <button
                    className={styles['quick-actions-column__button']}
                    type="button"
                    onClick={onRemoveFromCrate}
                    aria-label="Remove from Crate"
                    data-tip="Remove from Crate"
                    data-tip-show
                    data-delay-show="400"
                >
                    <RemoveFromCrate />
                </button>
                <button
                    className={classNames(styles['quick-actions-column__button'], styles['quick-actions-column__button--three-dots'], 'three-dots-button')}
                    type="button"
                    onClick={onOpenThreeDots}
                    aria-label="More"
                    data-tip="Show More"
                    data-tip-show
                    data-delay-show="400"
                >
                    <ThreeDotsIcon />
                </button>
            </div>
        );
    }

    const {
        openThreeDots,
        openPlaylists,
        downloadAlbum,
        addDownloadTrackToCrate,
    } = props;

    return (
        <div
            className={classNames('quick-actions-column', styles['quick-actions-column'], {
                'quick-actions-column--embedded': embedded,
                [styles['quick-actions-column--embedded']]: embedded,
            })}
        >
            <button
                className={classNames(styles['quick-actions-column__button'], styles['quick-actions-column__button--tablet-up-only'], 'three-dots-button')}
                type="button"
                onClick={openPlaylists}
                aria-label="Add to Playlist"
                data-tip="Add to Playlist"
                data-tip-show
                data-delay-show="400"
            >
                <AddToPlaylistIcon />
            </button>

            <button
                className={classNames(styles['quick-actions-column__button'], styles['quick-actions-column__button--tablet-up-only'])}
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // UX decision: if there is only one available version,
                    // skip opening the sheet and add it directly to the crate
                    if (downloadAlbum?.media?.length === 1 && addDownloadTrackToCrate) {
                        addDownloadTrackToCrate(downloadAlbum.media[0].id);
                    } else {
                        // if not open three dots preselected on add to crate option
                        openThreeDots(e, THREE_DOTS_SHEET_KEYS.ADD_TO_CRATE);
                    }
                }}
                aria-label="Add to Crate"
                data-tip="Add to Crate"
                data-tip-show
                data-delay-show="400"
            >
                <AddToCrate />
            </button>

            <button
                className={classNames(styles['quick-actions-column__button'], styles['quick-actions-column__button--three-dots'], 'three-dots-button')}
                type="button"
                aria-label="More"
                data-tip="Show More"
                data-tip-show
                data-delay-show="400"
                onClick={openThreeDots}
            >
                <ThreeDotsIcon />
            </button>
        </div>
    );
}
